// API utility functions

// Get API base URL from environment or use relative URLs for development proxy
export const getApiBaseUrl = () => {
  // For production builds on Cloudflare Pages, always use production URL
  // This is determined by checking if we're running in a browser and not on localhost
  if (typeof window !== 'undefined') {
    // Check if hostname contains Cloudflare Pages pattern or is not localhost
    const hostname = window.location.hostname;
    if (hostname.includes('pages.dev') || hostname.includes('ekdantarts') ||
        (hostname !== 'localhost' && hostname !== '127.0.0.1' && hostname !== '0.0.0.0')) {
      return 'https://api.ekdantarts.in';
    }
  }

  // Development: use environment variable or empty string for proxy
  try {
    if (import.meta.env) {
      const envBaseUrl = import.meta.env.VITE_API_BASE_URL;
      return envBaseUrl || ''; // Empty string for relative URLs (development proxy)
    }
  } catch (e) {
    // import.meta might not be available in some environments
  }
  return ''; // Fallback for relative URLs (development proxy)
};

/**
 * Fetch current user from backend
 * @returns {Promise<Object|null>} User object or null if not authenticated
 */
export const getUser = async () => {
  try {
    const baseUrl = getApiBaseUrl();
    const endpoint = `${baseUrl}/api/user`;
    console.log('API: Fetching user from', endpoint);
    const response = await fetch(endpoint, {
      method: 'GET',
      credentials: 'include', // Essential for session cookies over HTTPS
      headers: {
        'Content-Type': 'application/json',
      },
    });
    console.log('API: User fetch response status:', response.status);

    if (response.ok) {
      const user = await response.json();
      console.log('API: User data:', user);
      return user;
    }

    console.log('API: User fetch failed with status:', response.status);
    return null;
  } catch (error) {
    console.error('API: Error fetching user:', error);
    return null;
  }
};

/**
 * Check authentication status using /auth/me endpoint
 * @returns {Promise<{authenticated: boolean, user: Object|null}>}
 */
export const checkAuthStatus = async () => {
  try {
    const baseUrl = getApiBaseUrl();
    const endpoint = `${baseUrl}/api/auth/me`;
    console.log('API: Checking auth status from', endpoint);
    const response = await fetch(endpoint, {
      method: 'GET',
      credentials: 'include', // Essential for session cookies over HTTPS
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      const data = await response.json();
      console.log('API: Auth status:', data);
      return data;
    }

    console.log('API: Auth check failed with status:', response.status);
    return { authenticated: false, user: null };
  } catch (error) {
    console.error('API: Error checking auth status:', error);
    return { authenticated: false, user: null };
  }
};

/**
 * Fetch all inventory items
 * @returns {Promise<Array>} Array of inventory items
 */
export const getInventory = async () => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/inventory`, {
      credentials: 'include',
    });
    
    if (response.ok) {
      const data = await response.json();
      return data;
    }
    
    // Get the actual error message from response
    let errorMessage = 'Failed to fetch inventory';
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorMessage;
    } catch (e) {
      errorMessage = response.statusText || `HTTP ${response.status}`;
    }
    
    throw new Error(`${errorMessage} (Status: ${response.status})`);
  } catch (error) {
    console.error('Error fetching inventory:', error);
    // If it's a network error, provide more context
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      throw new Error('Cannot connect to server. Please check if the backend is running.');
    }
    throw error;
  }
};

/**
 * Search inventory by query term
 * @param {string} query - Search term
 * @returns {Promise<Array>} Array of matching inventory items
 */
export const searchInventory = async (query) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/inventory/search?q=${encodeURIComponent(query)}`, {
      credentials: 'include',
    });
    
    if (response.ok) {
      const data = await response.json();
      return data;
    }
    
    // Get the actual error message from response
    let errorMessage = 'Failed to search inventory';
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorMessage;
    } catch (e) {
      errorMessage = response.statusText || `HTTP ${response.status}`;
    }
    
    throw new Error(`${errorMessage} (Status: ${response.status})`);
  } catch (error) {
    console.error('Error searching inventory:', error);
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      throw new Error('Cannot connect to server. Please check if the backend is running.');
    }
    throw error;
  }
};

/**
 * Create a new inventory item
 * @param {Object} data - Inventory item data
 * @returns {Promise<Object>} Created inventory item
 */
export const createInventory = async (data) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/inventory`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (response.ok) {
      const item = await response.json();
      return item;
    }
    const error = await response.json();
    throw new Error(error.error || 'Failed to create inventory item');
  } catch (error) {
    console.error('Error creating inventory item:', error);
    throw error;
  }
};

/**
 * Update an inventory item
 * @param {string} id - Inventory item ID
 * @param {Object} data - Updated data
 * @returns {Promise<Object>} Updated inventory item
 */
export const updateInventory = async (id, data) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/inventory/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (response.ok) {
      const item = await response.json();
      return item;
    }
    const error = await response.json();
    throw new Error(error.error || 'Failed to update inventory item');
  } catch (error) {
    console.error('Error updating inventory item:', error);
    throw error;
  }
};

/**
 * Delete an inventory item
 * @param {string} id - Inventory item ID
 * @returns {Promise<Object>} Success message
 */
export const deleteInventory = async (id) => {
  try {
    console.log('API: Deleting inventory item with ID:', id);
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/inventory/${id}`, {
      method: 'DELETE',
      credentials: 'include',
      // Add timeout to prevent hanging requests
      signal: AbortSignal.timeout(10000), // 10 second timeout
    });

    console.log('API: Delete response status:', response.status);
    console.log('API: Delete response ok:', response.ok);

    // Read response as text first to avoid stream consumption issues
    const responseText = await response.text();
    console.log('API: Raw response (first 200 chars):', responseText.substring(0, 200));

    if (response.ok) {
      try {
        const result = JSON.parse(responseText);
        console.log('API: Delete success:', result);
        return result;
      } catch (jsonError) {
        console.log('API: Success response is not valid JSON');
        return { message: 'Item deleted successfully' };
      }
    }

    // Handle error responses
    let errorMessage = 'Failed to delete inventory item';

    // Check if response is HTML
    if (responseText.includes('<!DOCTYPE') || responseText.includes('<html>')) {
      console.log('API: Server returned HTML instead of JSON');
      errorMessage = `Server returned HTML instead of JSON. Status: ${response.status} ${response.statusText}`;
    } else {
      // Try to parse as JSON
      try {
        const errorData = JSON.parse(responseText);
        console.log('API: Delete error data:', errorData);
        errorMessage = errorData.error || errorMessage;
      } catch (jsonError) {
        console.log('API: Error response is not valid JSON');
        errorMessage = `Server error: ${response.status} ${response.statusText}`;
      }
    }

    throw new Error(errorMessage);
  } catch (error) {
    // Handle timeout errors specifically
    if (error.name === 'TimeoutError') {
      console.error('API: Delete request timed out');
      throw new Error('Request timed out. Please check your connection and try again.');
    }

    // Handle network errors
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      console.error('API: Network error during delete');
      throw new Error('Network error. Please check your internet connection.');
    }

    console.error('Error deleting inventory item:', error);
    throw error;
  }
};

/**
 * Clear all inventory data, orders, and counters
 * @param {string} password - Required password for confirmation
 * @returns {Promise<Object>} Success message
 */
export const clearAllData = async (password) => {
  try {
    console.log('API: Clearing all data...');
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/inventory/clear-all`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ password }),
      signal: AbortSignal.timeout(30000), // 30 second timeout for large operation
    });

    console.log('API: Clear all response status:', response.status);

    if (response.ok) {
      const result = await response.json();
      console.log('API: Clear all success:', result);
      return result;
    }

    // Handle error responses
    let errorMessage = 'Failed to clear all data';
    try {
      const errorData = await response.json();
      console.log('API: Clear all error data:', errorData);
      errorMessage = errorData.error || errorMessage;
    } catch (jsonError) {
      console.log('API: Clear all response not JSON, status:', response.status, response.statusText);
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }

    throw new Error(errorMessage);
  } catch (error) {
    // Handle timeout errors specifically
    if (error.name === 'TimeoutError') {
      console.error('API: Clear all request timed out');
      throw new Error('Request timed out. This operation may take longer due to large data volume.');
    }

    // Handle network errors
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      console.error('API: Network error during clear all');
      throw new Error('Network error. Please check your internet connection.');
    }

    console.error('Error clearing all data:', error);
    throw error;
  }
};
